import React, { useState } from 'react';
import {
  ShieldAlert, Check, X, Bell, CheckCircle2,
  ThumbsUp, ThumbsDown, Eye
} from 'lucide-react';
import { getDeviceIcon, getBlockerStatusBadge } from './helpers';

const BlockerActionButtons = ({ blocker, account, permissions, onAction, rejectState }) => {
  const { canResolveTech, canNotifyClient, canVerifyL3, canApprove, hasEditAccess, isInboundSales } = permissions;
  const { rejectingBlockerId, rejectNote, setRejectingBlockerId, setRejectNote } = rejectState;

  return (
    <div className="flex flex-wrap gap-2 justify-end">
      {canResolveTech && blocker.status === 'Active' && hasEditAccess && !isInboundSales && (
        <button
          onClick={() => onAction(blocker.id, 'resolve')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
          data-testid={`resolve-blocker-${blocker.id}`}
        >
          <Check size={14} /> Resolve
        </button>
      )}
      {canNotifyClient && blocker.status === 'Active' && account.current_team === 'Inbound Sales' && (
        <button
          onClick={() => onAction(blocker.id, 'client_notified')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-warning/10 text-warning rounded hover:bg-warning/20 transition-colors"
          data-testid={`notify-client-${blocker.id}`}
        >
          <Bell size={14} /> Client Notified
        </button>
      )}
      {canNotifyClient && blocker.status === 'Client Notified' && account.current_team === 'Inbound Sales' && (
        <button
          onClick={() => onAction(blocker.id, 'client_confirmed')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20 transition-colors"
          data-testid={`confirm-client-${blocker.id}`}
        >
          <CheckCircle2 size={14} /> Client Confirmed
        </button>
      )}
      {canApprove && blocker.status === 'Pending Manager Approval' && (
        <>
          <button
            onClick={() => onAction(blocker.id, 'approve')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-success/10 text-success rounded hover:bg-success/20 transition-colors"
            data-testid={`approve-blocker-${blocker.id}`}
          >
            <ThumbsUp size={14} /> Approve
          </button>
          {rejectingBlockerId === blocker.id ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Rejection reason"
                className="px-2 py-1 text-xs bg-background border border-border rounded text-text-primary w-32"
                data-testid={`reject-note-${blocker.id}`}
              />
              <button
                onClick={() => onAction(blocker.id, 'reject', rejectNote)}
                className="p-1 text-destructive hover:bg-destructive/10 rounded"
                data-testid={`confirm-reject-${blocker.id}`}
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => { setRejectingBlockerId(null); setRejectNote(''); }}
                className="p-1 text-text-secondary hover:bg-surface-highlight rounded"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setRejectingBlockerId(blocker.id)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive rounded hover:bg-destructive/20 transition-colors"
              data-testid={`reject-blocker-${blocker.id}`}
            >
              <ThumbsDown size={14} /> Reject
            </button>
          )}
        </>
      )}
      {canVerifyL3 && blocker.status === 'Pending L3 Verification' && account.current_team === 'Level 3 Tech' && (
        <button
          onClick={() => onAction(blocker.id, 'verify_cleared')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-success/10 text-success rounded hover:bg-success/20 transition-colors"
          data-testid={`verify-blocker-${blocker.id}`}
        >
          <Eye size={14} /> Verify Cleared
        </button>
      )}
    </div>
  );
};

const BlockersCard = ({ account, permissions, onBlockerAction, onAddBlocker }) => {
  const [rejectingBlockerId, setRejectingBlockerId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const handleAction = (blockerId, action, rejectionNote) => {
    onBlockerAction(blockerId, action, rejectionNote);
    if (action === 'reject') {
      setRejectingBlockerId(null);
      setRejectNote('');
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4" data-testid="blockers-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading font-semibold text-text-primary">
          Blockers
          {account.blockers.length > 0 && (
            <span className="ml-2 text-sm font-normal text-text-secondary">
              ({account.blockers.filter(b => b.status === 'Active').length} active)
            </span>
          )}
        </h2>
        {permissions.canAddBlockers && permissions.hasEditAccess && account.devices.length > 0 && (
          <button
            onClick={onAddBlocker}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-destructive/10 text-destructive rounded-md hover:bg-destructive/20 transition-colors"
            data-testid="add-blocker-btn"
          >
            <ShieldAlert size={16} /> Flag Blocker
          </button>
        )}
      </div>
      {account.blockers.length === 0 ? (
        <p className="text-text-secondary text-sm">No blockers flagged</p>
      ) : (
        <div className="space-y-3">
          {account.blockers.map((blocker) => {
            const Icon = getDeviceIcon(blocker.device_type);
            return (
              <div key={blocker.id} className="p-3 bg-background rounded-md border border-border" data-testid={`blocker-${blocker.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Icon size={20} className="text-text-secondary mt-0.5" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">{blocker.blocker_type}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getBlockerStatusBadge(blocker.status)}`}>
                          {blocker.status}
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary mt-1">
                        {blocker.device_type} - Raised by {blocker.raised_by_name}
                      </p>
                      {blocker.rejection_note && (
                        <p className="text-xs text-destructive mt-1 bg-destructive/10 px-2 py-1 rounded">
                          Rejection: {blocker.rejection_note}
                        </p>
                      )}
                    </div>
                  </div>
                  <BlockerActionButtons
                    blocker={blocker}
                    account={account}
                    permissions={permissions}
                    onAction={handleAction}
                    rejectState={{ rejectingBlockerId, rejectNote, setRejectingBlockerId, setRejectNote }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlockersCard;
